/**
 * Warehouse3DView - Interactive 3D visualization of a warehouse layout
 * Uses React Three Fiber for rendering 3D warehouse with racks, zones, aisles
 */
import React, { useRef, useMemo, useState, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import {
  OrbitControls,
  PerspectiveCamera,
  Text,
  Billboard,
  Line,
  Grid,
} from '@react-three/drei';
import * as THREE from 'three';
import { Box, IconButton, Tooltip, Paper, Typography, Chip } from '@mui/material';
import {
  Route as RouteIcon,
  Navigation as NavIcon,
  CenterFocusStrong as CenterIcon,
} from '@mui/icons-material';
import { MapElement } from './types';
import { PickingPath, NavigationGraph } from './pathTypes';

// ============================================
// Types
// ============================================

interface Warehouse3DViewProps {
  width: number;
  height: number;
  elements: MapElement[];
  navigationGraph?: NavigationGraph;
  pickingPath?: PickingPath;
  showPickingPath?: boolean;
  showNavigation?: boolean;
  startPoint?: { x: number; y: number; name?: string } | null;
  onRackClick?: (element: MapElement) => void;
}

// Scale factor: map units to 3D units (1 map unit = 0.01 3D units)
const SCALE = 0.01;
const RACK_HEIGHT_PER_SHELF = 0.5; // 3D units per shelf level
const ZONE_Y = 0.01;

// ============================================
// Helper: convert 2D map coords to 3D (x stays, y becomes z, add centering offset)
// ============================================
function mapTo3D(
  x: number,
  y: number,
  mapWidth: number,
  mapHeight: number,
  yOffset: number = 0
): [number, number, number] {
  return [
    (x - mapWidth / 2) * SCALE,
    yOffset,
    (y - mapHeight / 2) * SCALE,
  ];
}

// ============================================
// Rack 3D Component
// ============================================
interface Rack3DProps {
  element: MapElement;
  mapWidth: number;
  mapHeight: number;
  shelfCount: number;
  onClick?: () => void;
  isHighlighted?: boolean;
  highlightColor?: string;
}

const Rack3D: React.FC<Rack3DProps> = ({
  element,
  mapWidth,
  mapHeight,
  shelfCount,
  onClick,
  isHighlighted,
  highlightColor,
}) => {
  const meshRef = useRef<THREE.Mesh>(null!);
  const [hovered, setHovered] = useState(false);

  const realShelfCount = element.shelves?.length ?? shelfCount;
  const rackHeight = Math.max(1, realShelfCount) * RACK_HEIGHT_PER_SHELF;
  const w = element.dimensions.width * SCALE;
  const d = element.dimensions.height * SCALE;

  const centerX = element.position.x + element.dimensions.width / 2;
  const centerY = element.position.y + element.dimensions.height / 2;
  const pos = mapTo3D(centerX, centerY, mapWidth, mapHeight, rackHeight / 2);

  const baseColor = isHighlighted
    ? (highlightColor || '#ff9800')
    : hovered
    ? '#64b5f6'
    : element.color || '#90caf9';

  const shelfPlanes = useMemo(() => {
    const result: React.ReactNode[] = [];
    const count = realShelfCount;
    for (let i = 0; i < count; i++) {
      const shelfY = (i + 0.5) * RACK_HEIGHT_PER_SHELF - rackHeight / 2;
      const shelfData = element.shelves?.[i];
      const hasItems = shelfData && shelfData.totalItems > 0;
      const fillRatio = hasItems ? Math.min(1, shelfData.totalItems / 50) : 0;

      result.push(
        <group key={`shelf-${i}`}>
          {/* Shelf board */}
          <mesh position={[0, shelfY, 0]}>
            <boxGeometry args={[w - 0.02, 0.02, d - 0.02]} />
            <meshStandardMaterial
              color={hasItems ? '#78909c' : '#b0bec5'}
              transparent
              opacity={0.7}
            />
          </mesh>

          {/* Stock fill indicator */}
          {hasItems && (
            <mesh position={[0, shelfY + 0.01 + RACK_HEIGHT_PER_SHELF * 0.15, 0]}>
              <boxGeometry args={[(w - 0.06) * fillRatio, RACK_HEIGHT_PER_SHELF * 0.3, (d - 0.06) * 0.8]} />
              <meshStandardMaterial
                color="#42a5f5"
                transparent
                opacity={0.35}
              />
            </mesh>
          )}

          {/* Shelf level label */}
          <Billboard position={[0, shelfY + RACK_HEIGHT_PER_SHELF * 0.25, d / 2 + 0.005]}>
            <Text
              fontSize={0.07}
              color="#37474f"
              anchorX="center"
              anchorY="middle"
              font={undefined}
            >
              {shelfData ? `P${shelfData.level} · ${shelfData.totalItems}szt` : `P${i}`}
            </Text>
          </Billboard>

          {/* Top products — smaller */}
          {shelfData && shelfData.topProducts.length > 0 && (
            <Billboard position={[0, shelfY + RACK_HEIGHT_PER_SHELF * 0.06, d / 2 + 0.005]}>
              <Text
                fontSize={0.045}
                color="#546e7a"
                anchorX="center"
                anchorY="middle"
                maxWidth={w * 0.95}
                font={undefined}
              >
                {shelfData.topProducts.map(p => p.name.length > 14 ? p.name.slice(0, 14) + '..' : p.name).join(' | ')}
              </Text>
            </Billboard>
          )}
        </group>
      );
    }
    return result;
  }, [realShelfCount, rackHeight, w, d, element.shelves]);

  const posts = useMemo(() => {
    const postW = 0.03;
    const offsets = [
      [-w / 2 + postW / 2, -d / 2 + postW / 2],
      [w / 2 - postW / 2, -d / 2 + postW / 2],
      [-w / 2 + postW / 2, d / 2 - postW / 2],
      [w / 2 - postW / 2, d / 2 - postW / 2],
    ];
    return offsets.map(([ox, oz], idx) => (
      <mesh key={`post-${idx}`} position={[ox, 0, oz]}>
        <boxGeometry args={[postW, rackHeight, postW]} />
        <meshStandardMaterial color="#78909c" />
      </mesh>
    ));
  }, [w, d, rackHeight]);

  return (
    <group
      position={pos as any}
      rotation={[0, -(element.rotation || 0) * (Math.PI / 180), 0]}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
      onPointerOver={(e) => {
        e.stopPropagation();
        setHovered(true);
        document.body.style.cursor = 'pointer';
      }}
      onPointerOut={() => {
        setHovered(false);
        document.body.style.cursor = 'default';
      }}
    >
      {/* Semi-transparent rack body */}
      <mesh ref={meshRef}>
        <boxGeometry args={[w, rackHeight, d]} />
        <meshStandardMaterial
          color={baseColor}
          transparent
          opacity={hovered ? 0.5 : 0.3}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* Wireframe outline */}
      <mesh>
        <boxGeometry args={[w, rackHeight, d]} />
        <meshBasicMaterial
          color={isHighlighted ? highlightColor || '#ff9800' : '#546e7a'}
          wireframe
          transparent
          opacity={0.4}
        />
      </mesh>

      {/* Shelf planes with labels */}
      {shelfPlanes}

      {/* Corner posts */}
      {posts}

      {/* Rack code — top label */}
      <Billboard position={[0, rackHeight / 2 + 0.12, 0]}>
        <Text
          fontSize={0.15}
          color={isHighlighted ? '#ff6f00' : '#37474f'}
          anchorX="center"
          anchorY="middle"
          font={undefined}
          fontWeight="bold"
        >
          {element.code || element.name}
        </Text>
      </Billboard>

      {/* Stock summary below rack code */}
      {element.shelves && element.shelves.length > 0 && (
        <Billboard position={[0, rackHeight / 2 + 0.02, 0]}>
          <Text
            fontSize={0.08}
            color="#546e7a"
            anchorX="center"
            anchorY="middle"
            font={undefined}
          >
            {`${element.shelves.length} półek · ${element.stockCount ?? 0} szt`}
          </Text>
        </Billboard>
      )}
    </group>
  );
};

// ============================================
// Zone 3D Component (flat colored plane)
// ============================================
interface Zone3DProps {
  element: MapElement;
  mapWidth: number;
  mapHeight: number;
}

const Zone3D: React.FC<Zone3DProps> = ({ element, mapWidth, mapHeight }) => {
  const w = element.dimensions.width * SCALE;
  const d = element.dimensions.height * SCALE;
  const centerX = element.position.x + element.dimensions.width / 2;
  const centerY = element.position.y + element.dimensions.height / 2;
  const pos = mapTo3D(centerX, centerY, mapWidth, mapHeight, ZONE_Y);

  const color = element.color || '#e8f5e9';

  return (
    <group position={pos as any}>
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[w, d]} />
        <meshStandardMaterial
          color={color}
          transparent
          opacity={0.35}
          side={THREE.DoubleSide}
        />
      </mesh>
      {/* Zone border */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.001, 0]}>
        <planeGeometry args={[w, d]} />
        <meshBasicMaterial
          color={color}
          wireframe
          transparent
          opacity={0.5}
        />
      </mesh>
      {/* Label */}
      <Billboard position={[-w / 2 + 0.1, 0.05, -d / 2 + 0.1]}>
        <Text
          fontSize={0.12}
          color="#558b2f"
          anchorX="left"
          anchorY="middle"
          font={undefined}
        >
          {element.code || element.name}
        </Text>
      </Billboard>
    </group>
  );
};

// ============================================
// Aisle 3D Component (flat path on floor)
// ============================================
interface Aisle3DProps {
  element: MapElement;
  mapWidth: number;
  mapHeight: number;
}

const Aisle3D: React.FC<Aisle3DProps> = ({ element, mapWidth, mapHeight }) => {
  const w = element.dimensions.width * SCALE;
  const d = element.dimensions.height * SCALE;
  const centerX = element.position.x + element.dimensions.width / 2;
  const centerY = element.position.y + element.dimensions.height / 2;
  const pos = mapTo3D(centerX, centerY, mapWidth, mapHeight, 0.005);

  return (
    <group position={pos as any}>
      <mesh rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[w, d]} />
        <meshStandardMaterial
          color={element.color || '#fff8e1'}
          transparent
          opacity={0.5}
        />
      </mesh>
      <Billboard position={[0, 0.03, 0]}>
        <Text
          fontSize={0.08}
          color="#f57f17"
          anchorX="center"
          anchorY="middle"
          font={undefined}
        >
          {element.code || ''}
        </Text>
      </Billboard>
    </group>
  );
};

// ============================================
// Special Area 3D (COLD, HAZMAT, etc.)
// ============================================
interface SpecialArea3DProps {
  element: MapElement;
  mapWidth: number;
  mapHeight: number;
}

const SpecialArea3D: React.FC<SpecialArea3DProps> = ({ element, mapWidth, mapHeight }) => {
  const w = element.dimensions.width * SCALE;
  const d = element.dimensions.height * SCALE;
  const wallH = 0.6;
  const centerX = element.position.x + element.dimensions.width / 2;
  const centerY = element.position.y + element.dimensions.height / 2;
  const pos = mapTo3D(centerX, centerY, mapWidth, mapHeight, wallH / 2);

  const color = element.type === 'COLD' ? '#b3e5fc' : '#ffcdd2';

  return (
    <group position={pos as any}>
      {/* Walls */}
      <mesh>
        <boxGeometry args={[w, wallH, d]} />
        <meshStandardMaterial
          color={color}
          transparent
          opacity={0.2}
          side={THREE.DoubleSide}
        />
      </mesh>
      <mesh>
        <boxGeometry args={[w, wallH, d]} />
        <meshBasicMaterial color={color} wireframe transparent opacity={0.3} />
      </mesh>
      {/* Label */}
      <Billboard position={[0, wallH / 2 + 0.1, 0]}>
        <Text
          fontSize={0.12}
          color={element.type === 'COLD' ? '#01579b' : '#b71c1c'}
          anchorX="center"
          anchorY="middle"
          font={undefined}
        >
          {element.code || element.name}
        </Text>
      </Billboard>
    </group>
  );
};

// ============================================
// Generic Floor Element (SHELF, SLOT, FLOOR, etc.)
// ============================================
interface FloorElement3DProps {
  element: MapElement;
  mapWidth: number;
  mapHeight: number;
}

const FloorElement3D: React.FC<FloorElement3DProps> = ({ element, mapWidth, mapHeight }) => {
  const w = element.dimensions.width * SCALE;
  const d = element.dimensions.height * SCALE;
  const h = 0.15;
  const centerX = element.position.x + element.dimensions.width / 2;
  const centerY = element.position.y + element.dimensions.height / 2;
  const pos = mapTo3D(centerX, centerY, mapWidth, mapHeight, h / 2);

  return (
    <group position={pos as any}>
      <mesh>
        <boxGeometry args={[w, h, d]} />
        <meshStandardMaterial
          color={element.color || '#e0e0e0'}
          transparent
          opacity={0.4}
        />
      </mesh>
      <Billboard position={[0, h / 2 + 0.05, 0]}>
        <Text
          fontSize={0.08}
          color="#616161"
          anchorX="center"
          anchorY="middle"
          font={undefined}
        >
          {element.code || ''}
        </Text>
      </Billboard>
    </group>
  );
};

// ============================================
// Start Point Marker (3D pin)
// ============================================
interface StartMarker3DProps {
  x: number;
  y: number;
  name?: string;
  mapWidth: number;
  mapHeight: number;
}

const StartMarker3D: React.FC<StartMarker3DProps> = ({ x, y, name, mapWidth, mapHeight }) => {
  const pos = mapTo3D(x, y, mapWidth, mapHeight, 0);
  const meshRef = useRef<THREE.Mesh>(null!);

  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.position.y = 0.3 + Math.sin(state.clock.elapsedTime * 2) * 0.05;
    }
  });

  return (
    <group position={pos as any}>
      {/* Pin pole */}
      <mesh position={[0, 0.15, 0]}>
        <cylinderGeometry args={[0.02, 0.02, 0.3, 8]} />
        <meshStandardMaterial color="#4caf50" />
      </mesh>
      {/* Pin head (floating) */}
      <mesh ref={meshRef} position={[0, 0.3, 0]}>
        <sphereGeometry args={[0.08, 16, 16]} />
        <meshStandardMaterial color="#4caf50" emissive="#2e7d32" emissiveIntensity={0.3} />
      </mesh>
      {/* Label */}
      <Billboard position={[0, 0.5, 0]}>
        <Text
          fontSize={0.1}
          color="#2e7d32"
          anchorX="center"
          anchorY="middle"
          font={undefined}
        >
          {name || 'START'}
        </Text>
      </Billboard>
      {/* Ground circle */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.005, 0]}>
        <circleGeometry args={[0.15, 32]} />
        <meshStandardMaterial color="#4caf50" transparent opacity={0.3} />
      </mesh>
    </group>
  );
};

// ============================================
// Navigation Graph 3D
// ============================================
interface NavGraph3DProps {
  graph: NavigationGraph;
  mapWidth: number;
  mapHeight: number;
}

const NavGraph3D: React.FC<NavGraph3DProps> = ({ graph, mapWidth, mapHeight }) => {
  const nodePositions = useMemo(() => {
    const map = new Map<number, [number, number, number]>();
    graph.nodes.forEach((node) => {
      map.set(
        node.id,
        mapTo3D(node.position.x, node.position.y, mapWidth, mapHeight, 0.03)
      );
    });
    return map;
  }, [graph.nodes, mapWidth, mapHeight]);

  return (
    <group>
      {/* Edges */}
      {graph.edges.map((edge) => {
        const from = nodePositions.get(edge.fromNodeId);
        const to = nodePositions.get(edge.toNodeId);
        if (!from || !to) return null;
        return (
          <Line
            key={edge.id}
            points={[from, to]}
            color="#7e57c2"
            lineWidth={1.5}
            transparent
            opacity={0.5}
          />
        );
      })}
      {/* Nodes */}
      {graph.nodes.map((node) => {
        const pos = nodePositions.get(node.id);
        if (!pos) return null;
        const isEntry = node.nodeType === 'ENTRY';
        const isRackAccess = node.nodeType === 'RACK_ACCESS';
        return (
          <mesh key={node.id} position={pos as any}>
            <sphereGeometry args={[isEntry ? 0.06 : 0.04, 12, 12]} />
            <meshStandardMaterial
              color={isEntry ? '#4caf50' : isRackAccess ? '#ff9800' : '#7e57c2'}
              emissive={isEntry ? '#2e7d32' : '#4527a0'}
              emissiveIntensity={0.2}
            />
          </mesh>
        );
      })}
    </group>
  );
};

// ============================================
// Picking Path 3D (animated route line)
// ============================================
interface PickingPath3DProps {
  path: PickingPath;
  mapWidth: number;
  mapHeight: number;
}

const PickingPath3D: React.FC<PickingPath3DProps> = ({ path, mapWidth, mapHeight }) => {
  const points = useMemo(() => {
    return path.pathCoordinates.map((coord) =>
      mapTo3D(coord.x, coord.y, mapWidth, mapHeight, 0.06)
    );
  }, [path.pathCoordinates, mapWidth, mapHeight]);

  const stepMarkers = useMemo(() => {
    return path.steps.map((step) => {
      const pos = mapTo3D(
        step.location.position.x,
        step.location.position.y,
        mapWidth,
        mapHeight,
        0.1
      );
      return {
        pos,
        order: step.order,
        product: step.product.name,
        qtyToPick: step.quantityToPick,
        qtyAvailable: step.availableQuantity,
      };
    });
  }, [path.steps, mapWidth, mapHeight]);

  if (points.length < 2) return null;

  return (
    <group>
      <Line
        points={points}
        color="#ff6f00"
        lineWidth={3}
      />

      {stepMarkers.map((marker, idx) => (
        <group key={idx} position={marker.pos as any}>
          <mesh position={[0, 0.15, 0]}>
            <cylinderGeometry args={[0.01, 0.01, 0.3, 6]} />
            <meshStandardMaterial color="#ff6f00" transparent opacity={0.5} />
          </mesh>
          <mesh position={[0, 0.35, 0]}>
            <sphereGeometry args={[0.07, 12, 12]} />
            <meshStandardMaterial color="#ff6f00" emissive="#e65100" emissiveIntensity={0.3} />
          </mesh>
          <Billboard position={[0, 0.35, 0.08]}>
            <Text
              fontSize={0.08}
              color="white"
              anchorX="center"
              anchorY="middle"
              font={undefined}
            >
              {String(marker.order)}
            </Text>
          </Billboard>
          <Billboard position={[0, 0.5, 0]}>
            <Text
              fontSize={0.07}
              color="#e65100"
              anchorX="center"
              anchorY="middle"
              font={undefined}
            >
              {marker.product}
            </Text>
          </Billboard>
          <Billboard position={[0, 0.43, 0]}>
            <Text
              fontSize={0.06}
              color="#ff8f00"
              anchorX="center"
              anchorY="middle"
              font={undefined}
            >
              {`${marker.qtyToPick}/${marker.qtyAvailable} szt`}
            </Text>
          </Billboard>
        </group>
      ))}
    </group>
  );
};

// ============================================
// Floor Grid / Ground Plane
// ============================================
interface WarehouseFloor3DProps {
  mapWidth: number;
  mapHeight: number;
}

const WarehouseFloor3D: React.FC<WarehouseFloor3DProps> = ({ mapWidth, mapHeight }) => {
  const w = mapWidth * SCALE;
  const d = mapHeight * SCALE;

  return (
    <group>
      {/* Main floor plane */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.001, 0]}>
        <planeGeometry args={[w + 1, d + 1]} />
        <meshStandardMaterial color="#f5f5f5" />
      </mesh>
      {/* Grid */}
      <Grid
        position={[0, 0, 0]}
        args={[w + 1, d + 1]}
        cellSize={0.2}
        cellThickness={0.5}
        cellColor="#e0e0e0"
        sectionSize={1}
        sectionThickness={1}
        sectionColor="#bdbdbd"
        fadeDistance={20}
        fadeStrength={1}
        infiniteGrid={false}
      />
    </group>
  );
};

// ============================================
// Main Scene
// ============================================
interface WarehouseScene3DProps {
  elements: MapElement[];
  mapWidth: number;
  mapHeight: number;
  navigationGraph?: NavigationGraph;
  pickingPath?: PickingPath;
  showPickingPath: boolean;
  showNavigation: boolean;
  startPoint?: { x: number; y: number; name?: string } | null;
  onRackClick?: (element: MapElement) => void;
  highlightedRackCodes?: string[];
}

const WarehouseScene3D: React.FC<WarehouseScene3DProps> = ({
  elements,
  mapWidth,
  mapHeight,
  navigationGraph,
  pickingPath,
  showPickingPath,
  showNavigation,
  startPoint,
  onRackClick,
  highlightedRackCodes,
}) => {
  const getShelfCount = (el: MapElement): number => {
    if (el.shelves && el.shelves.length > 0) return el.shelves.length;
    if (el.type === 'COLD') return 2;
    if (el.type === 'HAZMAT') return 1;
    const area = el.dimensions.width * el.dimensions.height;
    if (area > 4000) return 6;
    if (area > 2000) return 5;
    return 4;
  };

  return (
    <>
      {/* Lighting */}
      <ambientLight intensity={0.6} />
      <directionalLight
        position={[5, 8, 5]}
        intensity={0.8}
        castShadow
        shadow-mapSize-width={2048}
        shadow-mapSize-height={2048}
      />
      <directionalLight position={[-3, 6, -3]} intensity={0.3} />

      {/* Floor */}
      <WarehouseFloor3D mapWidth={mapWidth} mapHeight={mapHeight} />

      {/* Elements */}
      {elements.map((el) => {
        switch (el.type) {
          case 'ZONE':
            return <Zone3D key={el.id} element={el} mapWidth={mapWidth} mapHeight={mapHeight} />;
          case 'AISLE':
            return <Aisle3D key={el.id} element={el} mapWidth={mapWidth} mapHeight={mapHeight} />;
          case 'RACK':
            return (
              <Rack3D
                key={el.id}
                element={el}
                mapWidth={mapWidth}
                mapHeight={mapHeight}
                shelfCount={getShelfCount(el)}
                onClick={() => onRackClick?.(el)}
                isHighlighted={highlightedRackCodes?.includes(el.code || '')}
                highlightColor="#ff9800"
              />
            );
          case 'COLD':
          case 'HAZMAT':
            if (el.shelves && el.shelves.length > 0) {
              return (
                <Rack3D
                  key={el.id}
                  element={el}
                  mapWidth={mapWidth}
                  mapHeight={mapHeight}
                  shelfCount={getShelfCount(el)}
                  onClick={() => onRackClick?.(el)}
                  isHighlighted={highlightedRackCodes?.includes(el.code || '')}
                  highlightColor={el.type === 'COLD' ? '#29b6f6' : '#ef5350'}
                />
              );
            }
            return (
              <SpecialArea3D
                key={el.id}
                element={el}
                mapWidth={mapWidth}
                mapHeight={mapHeight}
              />
            );
          default:
            return (
              <FloorElement3D
                key={el.id}
                element={el}
                mapWidth={mapWidth}
                mapHeight={mapHeight}
              />
            );
        }
      })}

      {/* Start point marker */}
      {startPoint && (
        <StartMarker3D
          x={startPoint.x}
          y={startPoint.y}
          name={startPoint.name}
          mapWidth={mapWidth}
          mapHeight={mapHeight}
        />
      )}

      {/* Navigation graph */}
      {showNavigation && navigationGraph && (
        <NavGraph3D graph={navigationGraph} mapWidth={mapWidth} mapHeight={mapHeight} />
      )}

      {/* Picking path */}
      {showPickingPath && pickingPath && (
        <PickingPath3D path={pickingPath} mapWidth={mapWidth} mapHeight={mapHeight} />
      )}
    </>
  );
};

// ============================================
// Main Warehouse3DView Component
// ============================================
const Warehouse3DView: React.FC<Warehouse3DViewProps> = ({
  width,
  height,
  elements,
  navigationGraph,
  pickingPath,
  showPickingPath = false,
  showNavigation = false,
  startPoint,
  onRackClick,
}) => {
  const [localShowNav, setLocalShowNav] = useState(showNavigation);
  const [localShowPath, setLocalShowPath] = useState(showPickingPath);
  const controlsRef = useRef<any>(null);

  useEffect(() => { setLocalShowPath(showPickingPath); }, [showPickingPath]);
  useEffect(() => { setLocalShowNav(showNavigation); }, [showNavigation]);

  // Calculate highlighted racks from picking path
  const highlightedRackCodes = useMemo(() => {
    if (!pickingPath || !localShowPath) return [];
    return pickingPath.steps
      .map((s) => s.rack?.code)
      .filter(Boolean) as string[];
  }, [pickingPath, localShowPath]);

  // Camera initial position
  const cameraDistance = Math.max(width, height) * SCALE * 0.8;

  const handleResetCamera = () => {
    if (controlsRef.current) {
      controlsRef.current.reset();
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        borderRadius: 2,
        overflow: 'hidden',
        bgcolor: '#f0f0f0',
      }}
    >
      {/* 3D Canvas */}
      <Canvas
        shadows
        gl={{ antialias: true, toneMapping: THREE.ACESFilmicToneMapping }}
        style={{ background: 'linear-gradient(180deg, #e3f2fd 0%, #f5f5f5 100%)' }}
      >
        <PerspectiveCamera
          makeDefault
          position={[0, cameraDistance * 0.6, cameraDistance * 0.5]}
          fov={50}
          near={0.1}
          far={100}
        />
        <OrbitControls
          ref={controlsRef}
          enableDamping
          dampingFactor={0.1}
          maxPolarAngle={Math.PI / 2 - 0.05}
          minDistance={0.5}
          maxDistance={30}
        />
        <fog attach="fog" args={['#f5f5f5', 15, 30]} />

        <WarehouseScene3D
          elements={elements}
          mapWidth={width}
          mapHeight={height}
          navigationGraph={navigationGraph}
          pickingPath={pickingPath}
          showPickingPath={localShowPath}
          showNavigation={localShowNav}
          startPoint={startPoint}
          onRackClick={onRackClick}
          highlightedRackCodes={highlightedRackCodes}
        />
      </Canvas>

      {/* Toolbar overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 8,
          left: 8,
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
        }}
      >
        <Paper
          elevation={1}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '8px',
            overflow: 'hidden',
          }}
        >
          <Tooltip title="Resetuj kamerę" placement="right">
            <IconButton
              size="small"
              onClick={handleResetCamera}
              sx={{ borderRadius: 0, width: 36, height: 36, color: 'text.secondary' }}
            >
              <CenterIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          {navigationGraph && (
            <Tooltip
              title={localShowNav ? 'Ukryj graf nawigacji' : 'Pokaż graf nawigacji'}
              placement="right"
            >
              <IconButton
                size="small"
                onClick={() => setLocalShowNav(!localShowNav)}
                sx={{
                  borderRadius: 0,
                  width: 36,
                  height: 36,
                  color: localShowNav ? 'primary.main' : 'text.secondary',
                }}
              >
                <NavIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}

          {pickingPath && (
            <Tooltip
              title={localShowPath ? 'Ukryj ścieżkę' : 'Pokaż ścieżkę'}
              placement="right"
            >
              <IconButton
                size="small"
                onClick={() => setLocalShowPath(!localShowPath)}
                sx={{
                  borderRadius: 0,
                  width: 36,
                  height: 36,
                  color: localShowPath ? 'warning.main' : 'text.secondary',
                }}
              >
                <RouteIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Paper>
      </Box>

      {/* Legend */}
      <Paper
        elevation={1}
        sx={{
          position: 'absolute',
          bottom: 8,
          left: 8,
          px: 1.5,
          py: 0.75,
          borderRadius: 2,
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          bgcolor: 'rgba(255,255,255,0.9)',
        }}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          3D
        </Typography>
        <Chip label="Regał" size="small" sx={{ bgcolor: '#90caf9', fontSize: 10, height: 20 }} />
        <Chip label="Strefa" size="small" sx={{ bgcolor: '#e8f5e9', fontSize: 10, height: 20 }} />
        <Chip label="Chłodnia" size="small" sx={{ bgcolor: '#b3e5fc', fontSize: 10, height: 20 }} />
        <Chip label="Niebezp." size="small" sx={{ bgcolor: '#ffcdd2', fontSize: 10, height: 20 }} />
        {localShowPath && (
          <Chip label="Ścieżka" size="small" sx={{ bgcolor: '#ff9800', color: 'white', fontSize: 10, height: 20 }} />
        )}
      </Paper>
    </Box>
  );
};

export default Warehouse3DView;
